import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useOnboardingStore } from '../../store/onboardingStore';

interface OnboardingTooltipProps {
  targetRef?: React.RefObject<HTMLElement>;
  title: string;
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  stepLabel?: string;
  show: boolean;
  onDismiss: () => void;
  allowClickThrough?: boolean;
  centerOnScreen?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  /**
   * Hide the X close button. Use for non-skippable cascades where the
   * user must walk through all steps (e.g., the invited-protagonist
   * micro-tutorial). Requires `actionLabel`/`onAction` so the user has
   * a way to advance.
   */
  hideClose?: boolean;
}

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  targetRef,
  title,
  description,
  position = 'bottom',
  stepLabel,
  show,
  onDismiss,
  allowClickThrough = false,
  centerOnScreen = false,
  actionLabel,
  onAction,
  secondaryLabel,
  onSecondary,
  hideClose = false,
}) => {
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [delayedShow, setDelayedShow] = useState(false);
  // Real measured tooltip dimensions (used for viewport clamping). Falls back
  // to maxWidth (320) until first paint completes the measurement.
  const [tooltipDims, setTooltipDims] = useState<{ width: number; height: number }>({ width: 320, height: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { skipAllSteps } = useOnboardingStore();

  // Delay showing by 300ms to prevent flash on rapid show/hide transitions
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setDelayedShow(true), 300);
      return () => clearTimeout(timer);
    }
    setDelayedShow(false);
  }, [show]);

  useEffect(() => {
    if (!show || !targetRef?.current) return;

    const updatePosition = () => {
      const rect = targetRef.current?.getBoundingClientRect();
      if (rect && (rect.width > 0 || rect.height > 0)) {
        setCoords({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      }
    };

    let rafId: number;
    const debouncedUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePosition);
    };

    updatePosition();
    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('scroll', debouncedUpdate, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('scroll', debouncedUpdate, true);
    };
  }, [show, targetRef]);

  // NOTE: any new hook calls MUST go above the early returns below.
  // Rules of Hooks: hooks must be called in the same order on every render.
  useLayoutEffect(() => {
    if (!delayedShow || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    if (rect.width !== tooltipDims.width || rect.height !== tooltipDims.height) {
      setTooltipDims({ width: rect.width, height: rect.height });
    }
  }, [delayedShow, coords, tooltipDims.width, tooltipDims.height]);

  if (!show || !delayedShow) return null;

  // For centerOnScreen without a target, we don't need coords
  const needsCoords = !centerOnScreen || !!targetRef?.current;
  if (needsCoords && !coords && !centerOnScreen) return null;

  const spotlightPadding = 8;
  const spotlightStyle: React.CSSProperties | null = coords ? {
    position: 'fixed',
    top: coords.top - spotlightPadding,
    left: coords.left - spotlightPadding,
    width: coords.width + spotlightPadding * 2,
    height: coords.height + spotlightPadding * 2,
    borderRadius: '16px',
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
    zIndex: allowClickThrough ? 78 : 80,
    pointerEvents: 'none',
  } : null;

  // Tooltip dimensions (updated by the layout effect above on first paint).
  const measuredTooltipWidth = tooltipDims.width;
  const measuredTooltipHeight = tooltipDims.height;

  const getTooltipStyle = (): React.CSSProperties => {
    const gap = 12;
    const edgeMargin = 12; // viewport-edge safety margin
    const base: React.CSSProperties = { position: 'fixed', zIndex: 82, maxWidth: '320px', width: 'max-content' };

    if (centerOnScreen) {
      return {
        ...base,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    if (!coords) return base;

    const halfW = measuredTooltipWidth / 2;
    const halfH = measuredTooltipHeight / 2;
    const minLeft = halfW + edgeMargin;
    const maxLeft = Math.max(minLeft, window.innerWidth - halfW - edgeMargin);
    const minTop = halfH + edgeMargin;
    const maxTop = Math.max(minTop, window.innerHeight - halfH - edgeMargin);

    switch (position) {
      case 'bottom': {
        const desiredLeft = coords.left + coords.width / 2;
        const clampedLeft = Math.max(minLeft, Math.min(maxLeft, desiredLeft));
        return { ...base, top: coords.top + coords.height + spotlightPadding + gap, left: clampedLeft, transform: 'translateX(-50%)' };
      }
      case 'top': {
        const desiredLeft = coords.left + coords.width / 2;
        const clampedLeft = Math.max(minLeft, Math.min(maxLeft, desiredLeft));
        return { ...base, bottom: window.innerHeight - coords.top + spotlightPadding + gap, left: clampedLeft, transform: 'translateX(-50%)' };
      }
      case 'left': {
        const desiredTop = coords.top + coords.height / 2;
        const clampedTop = Math.max(minTop, Math.min(maxTop, desiredTop));
        return { ...base, top: clampedTop, right: window.innerWidth - coords.left + spotlightPadding + gap, transform: 'translateY(-50%)' };
      }
      case 'right': {
        const desiredTop = coords.top + coords.height / 2;
        const clampedTop = Math.max(minTop, Math.min(maxTop, desiredTop));
        return { ...base, top: clampedTop, left: coords.left + coords.width + spotlightPadding + gap, transform: 'translateY(-50%)' };
      }
      default:
        return base;
    }
  };

  const getArrowStyle = (): { className: string; style?: React.CSSProperties } => {
    if (centerOnScreen) {
      if (!coords || !tooltipRef.current) return { className: '' };

      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const targetCenterX = coords.left + coords.width / 2;
      const targetCenterY = coords.top + coords.height / 2;
      const tooltipCenterX = tooltipRect.left + tooltipRect.width / 2;
      const tooltipCenterY = tooltipRect.top + tooltipRect.height / 2;

      const deltaX = targetCenterX - tooltipCenterX;
      const deltaY = targetCenterY - tooltipCenterY;

      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        if (deltaY > 0) {
          return {
            className: 'absolute -bottom-2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-stone-800',
            style: { left: '50%', transform: 'translateX(-50%)' }
          };
        } else {
          return {
            className: 'absolute -top-2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-stone-800',
            style: { left: '50%', transform: 'translateX(-50%)' }
          };
        }
      } else {
        if (deltaX > 0) {
          return {
            className: 'absolute -right-2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-stone-800',
            style: { top: '50%', transform: 'translateY(-50%)' }
          };
        } else {
          return {
            className: 'absolute -left-2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-stone-800',
            style: { top: '50%', transform: 'translateY(-50%)' }
          };
        }
      }
    }

    // After viewport-clamping the tooltip, the arrow needs to slide so it
    // still points at the actual target — otherwise the tooltip drifts away
    // from the target visually.
    if (!coords) return { className: '' };

    const halfW = measuredTooltipWidth / 2;
    const halfH = measuredTooltipHeight / 2;
    const edgeMargin = 12;
    const arrowMargin = 14; // keep arrow visually inside rounded tooltip corners

    switch (position) {
      case 'bottom':
      case 'top': {
        const desiredLeft = coords.left + coords.width / 2;
        const minLeft = halfW + edgeMargin;
        const maxLeft = Math.max(minLeft, window.innerWidth - halfW - edgeMargin);
        const clampedLeft = Math.max(minLeft, Math.min(maxLeft, desiredLeft));
        // Arrow's pixel offset from the tooltip's left edge.
        let arrowX = desiredLeft - (clampedLeft - halfW);
        arrowX = Math.max(arrowMargin, Math.min(measuredTooltipWidth - arrowMargin, arrowX));
        const baseCls = position === 'bottom'
          ? 'absolute -top-2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-stone-800'
          : 'absolute -bottom-2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-stone-800';
        return { className: baseCls, style: { left: `${arrowX}px` } };
      }
      case 'left':
      case 'right': {
        const desiredTop = coords.top + coords.height / 2;
        const minTop = halfH + edgeMargin;
        const maxTop = Math.max(minTop, window.innerHeight - halfH - edgeMargin);
        const clampedTop = Math.max(minTop, Math.min(maxTop, desiredTop));
        let arrowY = desiredTop - (clampedTop - halfH);
        arrowY = Math.max(arrowMargin, Math.min(measuredTooltipHeight - arrowMargin, arrowY));
        const baseCls = position === 'left'
          ? 'absolute -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-stone-800'
          : 'absolute -left-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-stone-800';
        return { className: baseCls, style: { top: `${arrowY}px` } };
      }
      default:
        return { className: '' };
    }
  };

  const arrowConfig = getArrowStyle();

  // Render via React Portal to document.body so position: fixed anchors
  // to the viewport. If the component renders inside a transformed
  // ancestor (mobile QuestionOverlayV2 has slide-in transforms), CSS
  // spec re-roots position: fixed to that ancestor — the spotlight
  // accidentally still aligns because it shares the same containing
  // block as the target, but the tooltip doesn't (the offset+gap math
  // assumes viewport coordinates). Portaling to body fixes both.
  if (typeof document === 'undefined') return null;

  const content = (
    <>
      {spotlightStyle && <div style={spotlightStyle} />}

      {/* Backdrop */}
      {allowClickThrough ? null : centerOnScreen && !coords ? (
        <div
          className="fixed inset-0 bg-black/50"
          style={{ zIndex: 80 }}
          onClick={onDismiss}
        />
      ) : (
        <div
          className="fixed inset-0 z-onboarding"
          onClick={onDismiss}
        />
      )}

      <div
        ref={tooltipRef}
        style={getTooltipStyle()}
        className="animate-fade-in"
      >
        {arrowConfig.className && <div className={arrowConfig.className} style={arrowConfig.style} />}
        <div className="bg-stone-800 text-white rounded-xl shadow-2xl p-4 max-w-[90vw]">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0 pt-0.5">
              {stepLabel && (
                <span className="text-[10px] font-bold bg-rose-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0">
                  {stepLabel}
                </span>
              )}
              <h3 className="text-sm font-bold truncate">{title}</h3>
            </div>
            {!hideClose && (
              <button
                onClick={onDismiss}
                className="-mt-1 -mr-1 p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0 touch-manipulation"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="text-xs text-stone-300 leading-relaxed mb-3">{description}</p>

          {actionLabel && onAction && (
            <div className="space-y-2">
              <button
                onClick={onAction}
                className="w-full text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-white py-2.5 px-4 rounded-lg transition-colors"
              >
                {actionLabel}
              </button>
              {secondaryLabel && onSecondary && (
                <button
                  onClick={onSecondary}
                  className="w-full text-xs font-medium bg-white/10 hover:bg-white/20 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  {secondaryLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default OnboardingTooltip;
